import { FC, ReactNode } from "react";

import { Row, Heading } from "@hightouch/htui";
import { PageSuspense } from "src/components/loading";

import { withCurrentPageCrumb } from "./crumbs";
import {
  NewPage,
  NewPageProps,
  PageCardContent,
  PageCardHeader,
} from "./new-page";

export type Props = Omit<NewPageProps, "flush"> & {
  /** Title/actions row content. */
  header?: ReactNode;
  /** On-screen page title rendered as a Heading. */
  heading?: string;
  actions?: ReactNode;
  /** Remove padding from the page card header. */
  headerFlush?: boolean;
  /** Remove padding from the page card content. */
  contentFlush?: boolean;
  /** The variant of the page card content. "flush" renders children directly
   *  without wrappers (no crumb bar). */
  variant?: "dark" | "light" | "flush";
  /** If true, the page card header will have a bottom border. */
  showHeaderBorder?: boolean;
  /** Removes the box shadow on the page card header. */
  removeHeaderShadow?: boolean;
};

export const Page: FC<Readonly<Props>> = ({
  children,
  header,
  outsideTopbar,
  sidebar,
  sidebarPanel,
  title,
  contain,
  heading,
  actions,
  crumbs,
  // L&F props
  headerFlush = false,
  contentFlush = false,
  variant = "light",
  showHeaderBorder = false,
  removeHeaderShadow = false,
}) => {
  const resolvedCrumbs = crumbs?.length
    ? withCurrentPageCrumb(crumbs, heading ?? title)
    : undefined;

  // Flush variant renders children directly without wrappers (no crumb bar)
  if (variant === "flush") {
    return (
      <NewPage
        contain={contain}
        title={title ?? heading}
        sidebar={sidebar}
        sidebarPanel={sidebarPanel}
        outsideTopbar={outsideTopbar}
        flush
      >
        <PageSuspense>{children}</PageSuspense>
      </NewPage>
    );
  }

  return (
    <NewPage
      contain={contain}
      title={title ?? heading}
      sidebar={sidebar}
      sidebarPanel={sidebarPanel}
      outsideTopbar={outsideTopbar}
      crumbs={resolvedCrumbs}
    >
      {(header || heading || actions) && (
        <PageCardHeader
          hasBorder={showHeaderBorder}
          flush={headerFlush}
          removeBoxShadow={removeHeaderShadow}
        >
          {header}
          {heading || actions ? (
            <Row align="center" gap={4} w="100%" bg="white">
              {heading && <Heading size="xl">{heading}</Heading>}
              {actions && (
                <Row align="center" ml="auto" gap={4}>
                  {actions}
                </Row>
              )}
            </Row>
          ) : null}
        </PageCardHeader>
      )}

      <PageCardContent flush={contentFlush} variant={variant}>
        <PageSuspense>{children}</PageSuspense>
      </PageCardContent>
    </NewPage>
  );
};
